import React, { useRef } from 'react';
import usePoiUpdateHandler from '../../components/pointsOfInterest/usePoiUpdateHandler';
import { Label } from '../../../common/labels/labelTypes';
import { TagEditable } from '@milanote/roma';
import { List } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { getEditingLabelId, getElementIdForSelectedLabel, getSelectedLabelIdForElement } from './labelSelectors';
import { labelRenameStart, labelRenameFinish, selectLabels } from '../../../common/labels/labelActions';
import { isFeatureEnabled } from '../../../common/users/utils/userPropertyUtils';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';
import { getCurrentUser } from '../../user/currentUserSelector';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { cx } from '@milanote/roma/utils/cx';
import { canWrite } from '../../../common/permissions/permissionUtil';
import { getSelectedElementIds } from '../selection/selectedElementsSelector';
import { updateLabel } from '../../../common/labels/labelActions';
import { LABEL_NAME_MAX_LENGTH } from '../../../common/labels/labelConstants';
import { useLabelSelection } from './hooks/useLabelSelectionHooks';
import { useSelectedLabelDocumentEvents } from './hooks/useSelectedLabelDocumentEventsHooks';
import { stopPropagationOnly } from '../../utils/domUtil';
import useSearchHighlightUpdates from './hooks/useSearchHighlightUpdates';
import { removeLabelFromElements } from './elementLabelActions';
import { deselectAllElements } from '../selection/selectionActions';
import { getThemeIsDarkMode } from '../../user/account/accountModal/interface/themeSettings/themeSelector';
import { getLabelOutlinePropertiesForElement } from './labelUtils';

type LabelElementPillsContainerProps = {
    labels: List<Label>;
    element: ImMNElement;
    selectedElementId?: string;
    size: 'xs' | 'sm' | 'md' | 'lg' | undefined;
    permissions: number;
    filterQuery?: string;
    labelScopeId?: string | null;
};

const SEARCH_HIGHLIGHT_CLASS = 'search-highlight';

const LabelElementPillsContainer: React.FC<LabelElementPillsContainerProps> = ({
    labels,
    filterQuery,
    size,
    element,
    selectedElementId,
    permissions,
    labelScopeId,
}) => {
    const dispatch = useDispatch();
    const dispatchFinishRename = () => dispatch(labelRenameFinish());
    const containerRef = useRef<HTMLDivElement | null>(null);

    const elementId = getElementId(element);
    const selectableElementId = selectedElementId || elementId;
    const selectedLabelId = useSelector(getSelectedLabelIdForElement(elementId));
    const selectedLabelsElementId = useSelector(getElementIdForSelectedLabel);
    const currentUser = useSelector(getCurrentUser);
    const selectedElementIds = useSelector(getSelectedElementIds);
    const editingLabelId = useSelector(getEditingLabelId);
    const isDarkModeTheme = useSelector(getThemeIsDarkMode);

    const isElementSingleSelected = selectedElementIds.size === 1 && selectedElementIds.includes(selectableElementId);
    const areLabelsEnabled = isFeatureEnabled(ExperimentId.tags, currentUser);
    const hasWritePermission = canWrite(permissions);
    const canSelectLabels = hasWritePermission && isElementSingleSelected && areLabelsEnabled;
    const isSelectedLabelInThisContainer = selectedLabelsElementId === selectableElementId && !!selectedLabelId;
    const isLabelEditingInThisContainer = editingLabelId !== null && isSelectedLabelInThisContainer;
    const isEditingLabelName = isLabelEditingInThisContainer;

    const handleMouseDown = (event: React.MouseEvent) => {
        if (canSelectLabels) return;

        // Prevent the label from taking focus
        event.preventDefault();
    };

    const handleLabelSelection = (labelId: string) => {
        if (!canSelectLabels || isLabelEditingInThisContainer) return;

        dispatchFinishRename();
        dispatch(selectLabels(labelId, elementId, selectableElementId));
    };

    const handleUpdateName = (labelId: string, name: string) => {
        if (!canSelectLabels) return;

        dispatch(updateLabel(labelId, { name }));
    };

    const handleBeginRename = (labelId: string) => {
        if (!canSelectLabels) return;

        if (!isSelectedLabelInThisContainer || selectedLabelId !== labelId) {
            dispatch(selectLabels(labelId, elementId, selectableElementId));
        }

        dispatch(labelRenameStart(labelId));
    };

    const handleEmptyBackspace = (labelId: string) => {
        if (!canSelectLabels) return;
        if (!isSelectedLabelInThisContainer || editingLabelId !== labelId) return;

        dispatch(removeLabelFromElements(List([element]), labelId));
        dispatch(deselectAllElements());
    };

    const onHighlightsChanged = usePoiUpdateHandler({
        contentInnerRef: containerRef,
        elementId,
        selector: `.${SEARCH_HIGHLIGHT_CLASS}`,
    });
    useSearchHighlightUpdates(onHighlightsChanged, filterQuery, labelScopeId);

    useLabelSelection({
        labels,
        selectedLabelId,
        isElementSingleSelected,
        isSelectedLabelInThisContainer,
    });

    useSelectedLabelDocumentEvents({
        containerRef,
        selectedLabelId,
        selectedLabelsElementId,
        selectableElementId,
        canSelectLabels,
        element,
        isSelectedLabelInThisContainer,
        isEditingLabelName,
    });

    const handleOnEditingChange = (labelId: string, nextIsEditing: boolean) => {
        if (nextIsEditing) {
            handleBeginRename(labelId);
            return;
        }

        dispatchFinishRename();
    };

    if (!labels?.size) return null;

    return (
        <div className="LabelElementPillsContainer contents" ref={containerRef}>
            {labels.map((label) => {
                if (!label) return null;

                const isSearchResult = labelScopeId
                    ? label._id === labelScopeId
                    : filterQuery && label.name.toLowerCase().includes(filterQuery.toLowerCase());
                const isSelected = selectedLabelId === label._id;
                const isEditing = editingLabelId === label._id && isSelectedLabelInThisContainer;
                const { hasSimilarColorToBackground, outlineColor } = getLabelOutlinePropertiesForElement(
                    label,
                    element,
                    isDarkModeTheme,
                );

                const tagBaseClassName = cx(
                    'inline-flex w-auto max-w-full transition-none',
                    isSelected && 'ring-2 ring-primary_focused',
                    isSearchResult && `${SEARCH_HIGHLIGHT_CLASS} ring-2 ring-search`,
                    hasSimilarColorToBackground && `outline ${outlineColor}`,
                );

                return (
                    <div
                        className={cx(
                            'LabelElementPill min-w-0 mb-1 mr-1',
                            size === 'xs' && 'flex items-center h-[19px] mt-[0.4rem] mb-0',
                        )}
                        key={label._id}
                        onMouseDown={handleMouseDown}
                        onClick={() => handleLabelSelection(label._id)}
                        onDoubleClick={stopPropagationOnly}
                    >
                        <TagEditable
                            dataTagId={label._id}
                            className={tagBaseClassName}
                            isDisabled={!hasWritePermission}
                            value={label.name}
                            color={label.color}
                            size={size}
                            maxLength={LABEL_NAME_MAX_LENGTH}
                            isEditing={isEditing}
                            onEditingChange={(nextIsEditing, details) => {
                                handleOnEditingChange(label._id, nextIsEditing);

                                if (!nextIsEditing && details?.reason === 'enter') {
                                    dispatch(deselectAllElements());
                                }
                            }}
                            onEmptyBackspace={() => handleEmptyBackspace(label._id)}
                            onCommit={(name) => handleUpdateName(label._id, name)}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default LabelElementPillsContainer;
